/**
 * Public Subscription Page
 * Accessible without login for iOS users who need to subscribe via web
 * If logged in, shows subscription purchase options
 * If not logged in, shows plans and prompts to login first
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { subscriptionAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Check, Crown, Zap, Building2, ArrowRight, Smartphone, Globe, Shield } from 'lucide-react';

const PLAN_ICONS = {
  free_trial: Zap,
  pro: Crown,
  enterprise: Building2,
};

const PLAN_COLORS = {
  free_trial: 'border-slate-500/30',
  pro: 'border-blue-500/50 ring-2 ring-blue-500/20',
  enterprise: 'border-purple-500/50',
};

export default function PublicSubscriptionPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState({});

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const res = await subscriptionAPI.getPlans();
      const plansData = res.data || [];
      setPlans(plansData);
      // Set default duration for each plan
      const defaults = {};
      plansData.forEach(p => {
        if (p.pricing) {
          defaults[p.id] = 'annual';
        }
      });
      setSelectedDuration(defaults);
    } catch (err) {
      console.error('Failed to load plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      // Save intended plan and redirect to login
      sessionStorage.setItem('intended_plan', planId);
      sessionStorage.setItem('intended_duration', selectedDuration[planId] || 'annual');
      toast.info('Effettua il login per completare l\'acquisto');
      navigate('/login');
      return;
    }

    setPurchasing(true);
    try {
      const duration = selectedDuration[planId] || 'annual';
      const res = await subscriptionAPI.createCheckout(planId, duration);
      
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Errore nella creazione del checkout');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante l\'acquisto');
    } finally {
      setPurchasing(false);
    }
  };

  const formatPrice = (pricing, duration) => {
    if (!pricing) return 'Gratuito';
    const price = pricing[duration];
    if (!price) return 'N/D';
    return `€${price.toFixed(2)}`;
  };

  const getPeriodLabel = (duration) => {
    return duration === 'annual' ? '/anno' : '/2 anni';
  };

  const isCurrentPlan = (planId) => {
    return user?.subscription_plan === planId;
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/obc-logo.png" alt="OBC Cloud" className="w-10 h-10" />
            <span className="font-bold text-xl" style={{ fontFamily: 'Manrope' }}>OBC Cloud</span>
          </div>
          {user ? (
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">{user.email}</span>
              <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
                Dashboard
              </Button>
            </div>
          ) : (
            <Button onClick={() => navigate('/login')}>
              Accedi
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4 text-primary border-primary/30">
            <Smartphone className="w-3 h-3 mr-1" /> Per utenti iOS
          </Badge>
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
            Scegli il tuo Piano
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Acquista il tuo abbonamento OBC Cloud direttamente dal web. 
            L'abbonamento sarà disponibile su tutti i tuoi dispositivi.
          </p>
        </div>

        {/* Current Plan Banner (if logged in) */}
        {user && (
          <Card className="mb-8 bg-primary/5 border-primary/20">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium">Piano attuale: <span className="text-primary">{user.subscription_plan || 'Free Trial'}</span></p>
                    <p className="text-sm text-muted-foreground">Stato: {user.subscription_status}</p>
                  </div>
                </div>
                {user.subscription_end && (
                  <Badge variant="outline">
                    Scade: {new Date(user.subscription_end).toLocaleDateString('it-IT')}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {plans.filter(p => p.id !== 'free_trial').map((plan) => {
            const Icon = PLAN_ICONS[plan.id] || Crown;
            const isCurrent = isCurrentPlan(plan.id);
            const isPopular = plan.id === 'pro';
            
            return (
              <Card 
                key={plan.id} 
                className={`relative ${PLAN_COLORS[plan.id]} ${isCurrent ? 'ring-2 ring-green-500/50' : ''}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-blue-500 text-white">Consigliato</Badge>
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-3 right-4">
                    <Badge className="bg-green-500 text-white">Attuale</Badge>
                  </div>
                )}
                
                <CardHeader className="text-center pb-2">
                  <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
                    plan.id === 'pro' ? 'bg-blue-500/20' : 
                    plan.id === 'enterprise' ? 'bg-purple-500/20' : 'bg-slate-500/20'
                  }`}>
                    <Icon className={`w-6 h-6 ${
                      plan.id === 'pro' ? 'text-blue-500' : 
                      plan.id === 'enterprise' ? 'text-purple-500' : 'text-slate-500'
                    }`} />
                  </div>
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  {plan.description && (
                    <CardDescription>{plan.description}</CardDescription>
                  )}
                </CardHeader>
                
                <CardContent className="space-y-6">
                  {/* Duration Selector */}
                  {plan.pricing && (
                    <div className="flex gap-2 p-1 bg-secondary/50 rounded-lg">
                      <button
                        className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                          selectedDuration[plan.id] === 'annual' 
                            ? 'bg-primary text-primary-foreground' 
                            : 'hover:bg-secondary'
                        }`}
                        onClick={() => setSelectedDuration(prev => ({ ...prev, [plan.id]: 'annual' }))}
                      >
                        Annuale
                      </button>
                      <button
                        className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                          selectedDuration[plan.id] === 'biannual' 
                            ? 'bg-primary text-primary-foreground' 
                            : 'hover:bg-secondary'
                        }`}
                        onClick={() => setSelectedDuration(prev => ({ ...prev, [plan.id]: 'biannual' }))}
                      >
                        Biennale
                      </button>
                    </div>
                  )}

                  {/* Price */}
                  <div className="text-center">
                    <span className="text-4xl font-bold">
                      {formatPrice(plan.pricing, selectedDuration[plan.id] || 'annual')}
                    </span>
                    <span className="text-muted-foreground">
                      {getPeriodLabel(selectedDuration[plan.id] || 'annual')}
                    </span>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3">
                    {(plan.features || []).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button 
                    className="w-full" 
                    size="lg"
                    variant={isCurrent ? 'outline' : (isPopular ? 'default' : 'secondary')}
                    disabled={isCurrent || purchasing}
                    onClick={() => handleSubscribe(plan.id)}
                  >
                    {purchasing ? (
                      <span className="animate-spin mr-2">...</span>
                    ) : isCurrent ? (
                      'Piano Attuale'
                    ) : (
                      <>
                        {user ? 'Acquista Ora' : 'Seleziona'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Section */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className="bg-card/50">
            <CardContent className="pt-6 text-center">
              <Globe className="w-8 h-8 mx-auto mb-3 text-primary" />
              <h3 className="font-semibold mb-2">Multi-piattaforma</h3>
              <p className="text-sm text-muted-foreground">
                L'abbonamento funziona su iOS, Android e Web
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardContent className="pt-6 text-center">
              <Shield className="w-8 h-8 mx-auto mb-3 text-primary" />
              <h3 className="font-semibold mb-2">Pagamento Sicuro</h3>
              <p className="text-sm text-muted-foreground">
                Transazioni protette con Stripe
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardContent className="pt-6 text-center">
              <Zap className="w-8 h-8 mx-auto mb-3 text-primary" />
              <h3 className="font-semibold mb-2">Attivazione Immediata</h3>
              <p className="text-sm text-muted-foreground">
                Accesso istantaneo dopo il pagamento
              </p>
            </CardContent>
          </Card>
        </div>

        {/* FAQ */}
        <Card>
          <CardHeader>
            <CardTitle>Domande Frequenti</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium mb-1">Perché devo acquistare dal sito web?</h4>
              <p className="text-sm text-muted-foreground">
                Per offrirti i migliori prezzi e la massima flessibilità, gestiamo gli abbonamenti direttamente dal nostro sito web. L'abbonamento sarà disponibile su tutti i tuoi dispositivi.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-1">Come accedo all'abbonamento dall'app iOS?</h4>
              <p className="text-sm text-muted-foreground">
                Dopo l'acquisto, effettua il login nell'app iOS con lo stesso account. L'abbonamento verrà riconosciuto automaticamente.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-1">Posso annullare in qualsiasi momento?</h4>
              <p className="text-sm text-muted-foreground">
                Sì, puoi gestire o annullare il tuo abbonamento in qualsiasi momento dalla sezione Profilo.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} OBC Cloud. Tutti i diritti riservati.</p>
          <div className="flex justify-center gap-4 mt-2">
            <a href="/privacy" className="hover:text-primary">Privacy Policy</a>
            <span>•</span>
            <a href="mailto:support@obcevo.com" className="hover:text-primary">Supporto</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
